/**
 * t289u / t290u: each call shows as ONE centred line in the chat, paired by call id (never by guessing):
 * "Arlo Fisher started an audio call · 11:22 · 16 secs", "You called · no answer · 11:23",
 * "Missed audio call from Arlo Fisher · 11:23", "Arlo Fisher started an audio call · 11:22 · in progress" (+ Join).
 * Every line names who started it ("You" for the viewer). Outcome fields come from the "call ended" note's metadata
 * (calls.service end(); older notes backfilled by migration 202612070001).
 * t313u: moved here from apps/web/src/features/chat so the web and the native app draw calls the same way.
 */
import type { Message } from './types';
import { callDuration, withArticle } from './callText';

type CallMeta = {
  event?: string; call_id?: string; media?: 'audio' | 'video'; duration_s?: number | null; call_messages?: number;
  answered?: boolean; reason?: string | null; missed_user_ids?: string[]; declined_user_ids?: string[];
};
type CallMsg = Pick<Message, 'id' | 'kind' | 'body' | 'sender_id' | 'created_at' | 'metadata'>;

const meta = (m: CallMsg) => (m.metadata ?? {}) as CallMeta;
/** 'call.started' / 'call.ended' for a call note with a call id, otherwise null. */
export function callEvent(m: CallMsg): 'call.started' | 'call.ended' | null {
  if (m.kind !== 'system') return null;
  const x = meta(m);
  return (x.event === 'call.started' || x.event === 'call.ended') && x.call_id ? x.event : null;
}

/** Pair each started note with its ended note by call id. `ended`: started id -> ended note; `hidden`: ended notes shown on their started line. */
export function pairCalls<T extends CallMsg>(list: readonly T[]): { ended: Map<string, T>; hidden: Set<string> } {
  const starts = new Map<string, T>();
  const ended = new Map<string, T>();
  const hidden = new Set<string>();
  for (const m of list) {
    const ev = callEvent(m);
    if (ev === 'call.started') starts.set(meta(m).call_id!, m);
    else if (ev === 'call.ended') {
      const s = starts.get(meta(m).call_id!);
      if (s) { ended.set(s.id, m); hidden.add(m.id); }
    }
  }
  return { ended, hidden };
}

/** A call older than this with no ended note is not "in progress" (the server ends stale calls after 12 hours). */
const LIVE_MAX_MS = 12 * 3600_000;

export type CallState = 'live' | 'answered' | 'no_answer' | 'missed' | 'declined' | 'ended';
export interface CallLine {
  callId: string; media: 'audio' | 'video'; starterId: string; mine: boolean;
  /** When it started (the started note, or the ended note minus the duration when the start isn't loaded). */
  at: string; state: CallState;
  /** Text before the starter's name, whether the name shows (never for the viewer, who reads "You"), and after it. */
  prefix: string; person: boolean; suffix: string;
  /** Extra parts after the time: the duration, "no answer", "declined", "in progress", messages during the call. */
  details: string[];
  /** The viewer missed or declined it: a calm red-ish tone. */
  alert: boolean;
}

function mediaOf(start: CallMsg | null, end: CallMsg | null): 'audio' | 'video' {
  const m = (end && meta(end).media) || (start && meta(start).media);
  if (m === 'audio' || m === 'video') return m;
  return /video/i.test(start?.body ?? end?.body ?? '') ? 'video' : 'audio';
}

/** Older notes without duration_s: read it from the text ("call ended (2 mins · …)"). */
function durationText(end: CallMsg): string | null {
  const s = meta(end).duration_s;
  if (typeof s === 'number' && Number.isFinite(s)) return callDuration(s);
  const t = /^call ended \(([^)·]+?)(?: ·|\))/i.exec(end.body)?.[1]?.trim();
  if (!t) return null;
  const mins = /^(\d+) mins?$/.exec(t);
  return mins ? callDuration(Number(mins[1]) * 60) : t;
}

export function describeCall(start: CallMsg | null, end: CallMsg | null, viewer: string | null | undefined, now = Date.now()): CallLine | null {
  const base = start ?? end;
  if (!base) return null;
  const callId = meta(base).call_id ?? base.id;
  const media = mediaOf(start, end);
  const starterId = base.sender_id;
  const mine = Boolean(viewer) && starterId === viewer;
  const em = end ? meta(end) : null;
  const at = start ? start.created_at
    : new Date(Date.parse(end!.created_at) - (typeof em?.duration_s === 'number' ? em.duration_s * 1000 : 0)).toISOString();
  const started = { prefix: mine ? `You started ${withArticle(`${media} call`)}` : '', person: !mine, suffix: mine ? '' : ` started ${withArticle(`${media} call`)}` };
  const msgs = em?.call_messages ? [`${em.call_messages} message${em.call_messages === 1 ? '' : 's'}`] : [];
  const line = (state: CallState, words: Pick<CallLine, 'prefix' | 'person' | 'suffix'>, details: string[], alert = false): CallLine =>
    ({ callId, media, starterId, mine, at, state, ...words, details, alert });

  if (!end) {
    const live = now - Date.parse(at) < LIVE_MAX_MS;
    return line(live ? 'live' : 'ended', started, live ? ['in progress'] : []);
  }
  const dur = durationText(end);
  if (em?.answered === false) {
    const missedMe = Boolean(viewer && em.missed_user_ids?.includes(viewer));
    const declinedMe = Boolean(viewer && em.declined_user_ids?.includes(viewer));
    if (mine) return line(em.reason === 'declined' ? 'declined' : 'no_answer', { prefix: 'You called', person: false, suffix: '' }, [em.reason === 'declined' ? 'declined' : 'no answer']);
    if (declinedMe) return line('declined', { prefix: `You declined ${withArticle(`${media} call`)} from `, person: true, suffix: '' }, []);
    if (em.reason === 'declined') return line('declined', { prefix: '', person: true, suffix: ' called' }, ['declined']);
    return line('missed', { prefix: `Missed ${media} call from `, person: true, suffix: '' }, [], missedMe);
  }
  // Answered, or an older note with no outcome recorded: the duration when there is one.
  return line(em?.answered ? 'answered' : 'ended', started, [...(dur ? [dur] : []), ...msgs]);
}
